import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import qs from 'qs';
import Button from 'src/components/Button';
import withWebClientValidation from 'src/components/HOC/withWebClientValidation';
import ResizingTextBase from 'src/components/ResizingText';
import useIntersection from 'src/hooks/useIntersection';
import useQueryParams from 'src/hooks/useQueryParams';
import { hexToRgb } from 'src/utils/common';

/**
 * 별도 디자인 대응이 필요한 최소 해상도 케이스
 */
const MIN_SCREEN_WIDTH = 375;
const Root = styled.div`
  width: var(--100vw);
  height: var(--100vh);
  display: flex;
  flex-direction: column;
`;
const Main = styled.div`
  position: relative;
  flex: 1;
  overflow-y: scroll;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px;
  min-height: 100%;

  ${({
  theme
}) => theme.screenSize.breakpointDown(MIN_SCREEN_WIDTH)} {
    justify-content: flex-start;
  }
`;
const LoadingSpinner = styled.img`
  width: 120px;
  height: 120px;
`;
const ResizingText = styled(ResizingTextBase)`
  // 요게 없이 필요한 만큼 width 차지 시 width 부족하다고 오판단되어 불필요한 리사이징 발생 가능
  width: 100%;
  word-break: keep-all;
  word-wrap: break-word;
  text-align: center;
`;
const Title = styled(ResizingText)`
  font-weight: 700;
  font-size: 30px;
  line-height: 40px;
  color: ${({
  theme
}) => theme.color.contentDeep};

  ${({
  theme
}) => theme.screenSize.breakpointDown(MIN_SCREEN_WIDTH)} {
    font-size: 22px;
  }
`;
const Description = styled(ResizingText)`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content10};

  ${({
  theme
}) => theme.screenSize.breakpointDown(MIN_SCREEN_WIDTH)} {
    font-size: 12px;
  }
`;
const BottomDimTrigger = styled.div`
  width: 100%;
  height: 1px;
  margin-top: -1px;
  z-index: -1;
`;
const BottomDim = styled.div`
  position: sticky;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 56px;
  background: ${({
  theme
}) => `linear-gradient(180deg, rgba(${hexToRgb(theme.color.bg1)}, 0) 0%, ${theme.color.bg1} 100%)`};
`;
const Footer = styled.footer`
  flex-shrink: 0;
  padding: 16px 24px 24px 24px;
`;
const AppExternalPaymentSuccessView = () => {
  const {
    orderId
  } = useQueryParams<{
    orderId: string;
  }>() || {};
  const [isBottomReached, setIsBottomReached] = useState(true);
  const handleBottomIntersection = useCallback<IntersectionObserverCallback>(([entry]) => {
    setIsBottomReached(entry.isIntersecting);
  }, []);
  const {
    observerRef: bottomObserverRef
  } = useIntersection(handleBottomIntersection);
  const {
    t
  } = useTranslation();
  const openDeepLink = useCallback(() => {
    if (!orderId) return;
    const PAYMENT_SUCCESS_DEEP_LINK = 'azarlive://payment_complete';
    window.location.href = PAYMENT_SUCCESS_DEEP_LINK + qs.stringify({
      orderId
    }, {
      addQueryPrefix: true
    });
  }, [orderId]);
  useEffect(() => {
    openDeepLink();
  }, [openDeepLink]);
  return <Root data-sentry-element="Root" data-sentry-component="AppExternalPaymentSuccessView" data-sentry-source-file="AppExternalPaymentSuccess.tsx">
      <Main data-sentry-element="Main" data-sentry-source-file="AppExternalPaymentSuccess.tsx">
        <Content data-sentry-element="Content" data-sentry-source-file="AppExternalPaymentSuccess.tsx">
          <LoadingSpinner src='/images/3d_loading.webp' alt='Loading Spinner' data-sentry-element="LoadingSpinner" data-sentry-source-file="AppExternalPaymentSuccess.tsx" />
          <Title maxLines={3} minFontSize={22} data-sentry-element="Title" data-sentry-source-file="AppExternalPaymentSuccess.tsx">
            {t('payment_options_complete_title')}
          </Title>
          <Description maxLines={6} minFontSize={12} data-sentry-element="Description" data-sentry-source-file="AppExternalPaymentSuccess.tsx">
            {t('payment_options_complete_desc')}
          </Description>
        </Content>
        <BottomDimTrigger ref={bottomObserverRef} data-sentry-element="BottomDimTrigger" data-sentry-source-file="AppExternalPaymentSuccess.tsx" />
        {!isBottomReached && <BottomDim />}
      </Main>
      <Footer data-sentry-element="Footer" data-sentry-source-file="AppExternalPaymentSuccess.tsx">
        <Button id='AppExternalPaymentSuccess-ConfirmButton' onClick={openDeepLink} data-sentry-element="Button" data-sentry-source-file="AppExternalPaymentSuccess.tsx">
          {t('payment_options_complete_button')}
        </Button>
      </Footer>
    </Root>;
};
export default withWebClientValidation(AppExternalPaymentSuccessView);